<template>
  <div class="row-approvals">
    <div class="row-approvals__head">
      <div class="row-approvals__head-buttons">
        <Button
          type="primary"
          icon="iconoir-edit-pencil"
          @click="$refs.sendModal.show()"
        >
          {{ $t('rowApprovals.send') }}
        </Button>
        <Button
          type="secondary"
          icon="iconoir-task-list"
          @click="$refs.checklistModal.show()"
        >
          {{ $t('rowChecklists.create') }}
        </Button>
        <Button
          type="secondary"
          icon="iconoir-star"
          @click="$refs.ratingModal.show()"
        >
          {{ $t('rowRatings.request') }}
        </Button>
      </div>
      <div class="row-approvals__head-hint">
        {{ $t('rowApprovals.headHint') }}
      </div>
      <a class="row-approvals__form-link" @click="openRequestForm">
        <i class="iconoir-page-edit"></i>
        {{ $t('rowChecklists.requestForm') }}
        <span v-if="requestFormUrl" class="row-approvals__form-ready">
          · {{ $t('rowChecklists.requestFormReady') }}</span
        >
      </a>
      <div v-if="requestFormUrl" class="send-approval__link">
        <input
          class="input"
          :value="requestFormUrl"
          readonly
          @focus="$event.target.select()"
        />
        <Button type="secondary" icon="iconoir-copy" @click="copyText(requestFormUrl)">
          {{ $t('sendApproval.copy') }}
        </Button>
      </div>
    </div>

    <div v-if="loading" class="row-approvals__loading">
      <div class="loading"></div>
    </div>

    <div
      v-else-if="
        approvals.length === 0 && checklists.length === 0 && ratings.length === 0
      "
      class="row-approvals__empty"
    >
      <i class="iconoir-check-circle row-approvals__empty-icon"></i>
      <p>{{ $t('rowApprovals.empty') }}</p>
    </div>

    <div v-if="!loading && ratings.length > 0" class="row-approvals__list">
      <div class="row-approvals__section">
        {{ $t('rowRatings.section') }}
      </div>
      <div
        v-for="rating in ratings"
        :key="'rating' + rating.id"
        class="row-approvals__item"
      >
        <div class="row-approvals__item-head">
          <span class="row-approvals__item-title">{{ rating.title }}</span>
          <span
            v-if="rating.status === 'answered'"
            class="row-approvals__chip row-approvals__score"
            :class="`row-approvals__score--${scoreKind(rating.score)}`"
          >
            {{ rating.score }}/10
          </span>
          <span v-else class="row-approvals__chip" :class="`row-approvals__chip--${rating.status}`">
            {{ $t(`rowRatings.status.${rating.status}`) }}
          </span>
        </div>
        <div v-if="rating.status === 'answered'" class="row-approvals__meta">
          {{ rating.answered_by || $t('rowRatings.someone') }} ·
          {{ formatDate(rating.answered_on) }}
          <em v-if="rating.comment">“{{ rating.comment }}”</em>
        </div>
        <div v-else class="row-approvals__meta">
          {{ rating.contact_name || '—' }} · {{ formatDate(rating.created_on) }}
        </div>
        <div v-if="rating.status !== 'answered'" class="row-approvals__actions">
          <a class="row-approvals__action" @click="copyText(rating.public_url)">
            <i class="iconoir-copy"></i> {{ $t('rowApprovals.copyLink') }}
          </a>
          <a class="row-approvals__action" @click="openRatingWhatsApp(rating)">
            <i class="iconoir-chat-bubble"></i> WhatsApp
          </a>
          <a
            class="row-approvals__action row-approvals__action--danger"
            @click="toggleRatingRevoked(rating)"
          >
            <i :class="rating.status === 'revoked' ? 'iconoir-refresh' : 'iconoir-lock'"></i>
            {{
              rating.status === 'revoked'
                ? $t('clientDigest.reactivate')
                : $t('rowApprovals.revoke')
            }}
          </a>
        </div>
      </div>
    </div>

    <div v-if="!loading && checklists.length > 0" class="row-approvals__list">
      <div class="row-approvals__section">
        {{ $t('rowChecklists.section') }}
      </div>
      <div
        v-for="checklist in checklists"
        :key="`c${checklist.id}`"
        class="row-approvals__item"
      >
        <div class="row-approvals__item-head">
          <span class="row-approvals__item-title">{{ checklist.title }}</span>
          <span
            class="row-approvals__chip"
            :class="`row-approvals__chip--${checklistChip(checklist)}`"
          >
            {{ $t(`rowChecklists.status.${checklistChip(checklist)}`) }}
          </span>
        </div>
        <div class="row-approvals__progress">
          <div
            class="row-approvals__progress-fill"
            :style="{
              width: `${
                checklist.progress.total
                  ? Math.round(
                      (checklist.progress.done / checklist.progress.total) * 100
                    )
                  : 0
              }%`,
            }"
          ></div>
        </div>
        <div class="row-approvals__meta">
          {{
            $t('rowChecklists.meta', {
              done: checklist.progress.done,
              total: checklist.progress.total,
            })
          }}
          <template v-if="checklist.contact_name">
            · {{ checklist.contact_name }}</template
          >
        </div>
        <ul class="row-approvals__check-items">
          <li
            v-for="item in checklist.items"
            :key="item.id"
            :class="{ 'row-approvals__check-item--done': item.done }"
          >
            <a
              class="row-approvals__check-toggle"
              :title="$t('rowChecklists.toggleHint')"
              @click="toggleItem(checklist, item)"
              ><i
                :class="
                  item.done ? 'iconoir-check-circle' : 'iconoir-circle'
                "
              ></i
            ></a>
            <span class="row-approvals__check-label">
              {{ item.label }}
              <a
                v-for="file in item.files"
                :key="file.name"
                :href="file.url"
                target="_blank"
                rel="noopener"
                class="row-approvals__check-file"
                ><i class="iconoir-attachment"></i>{{ file.visible_name }}</a
              >
              <em v-if="item.answer">“{{ item.answer }}”</em>
              <span v-if="item.manual" class="row-approvals__manual-flag">{{
                $t('rowApprovals.manualFlag')
              }}</span>
            </span>
          </li>
        </ul>
        <div class="row-approvals__actions">
          <a class="row-approvals__action" @click="copyText(checklist.public_url)">
            <i class="iconoir-copy"></i> {{ $t('rowApprovals.copyLink') }}
          </a>
          <a class="row-approvals__action" @click="openChecklistWhatsApp(checklist)">
            <i class="iconoir-chat-bubble"></i> WhatsApp
          </a>
          <a
            class="row-approvals__action"
            :class="{
              'row-approvals__action--danger': checklist.status !== 'revoked',
            }"
            @click="toggleChecklistRevoked(checklist)"
          >
            <i class="iconoir-lock"></i>
            {{
              checklist.status === 'revoked'
                ? $t('rowApprovals.reactivate')
                : $t('rowApprovals.revoke')
            }}
          </a>
        </div>
      </div>
    </div>

    <div v-if="!loading && approvals.length > 0" class="row-approvals__list">
      <div v-if="checklists.length > 0" class="row-approvals__section">
        {{ $t('rowChecklists.approvalsSection') }}
      </div>
      <div
        v-for="approval in approvals"
        :key="approval.id"
        class="row-approvals__item"
      >
        <div class="row-approvals__item-head">
          <span class="row-approvals__item-title">{{ approval.title }}</span>
          <span
            class="row-approvals__chip"
            :class="`row-approvals__chip--${chipKind(approval).replace('_', '-')}`"
          >
            {{ chipLabel(approval) }}
          </span>
        </div>
        <div class="row-approvals__meta">
          {{
            $t('rowApprovals.meta', {
              rounds: approval.versions.length,
              accesses: approval.access_count,
            })
          }}
          <template v-if="approval.contact_name">
            · {{ approval.contact_name }}</template
          >
        </div>

        <div class="row-approvals__actions">
          <a class="row-approvals__action" @click="copyLink(approval)">
            <i class="iconoir-copy"></i> {{ $t('rowApprovals.copyLink') }}
          </a>
          <a class="row-approvals__action" @click="openWhatsApp(approval)">
            <i class="iconoir-chat-bubble"></i> WhatsApp
          </a>
          <a
            class="row-approvals__action"
            :class="{
              'row-approvals__action--disabled':
                !approval.contact_email || sendingEmailId === approval.id,
            }"
            @click="sendEmail(approval)"
          >
            <i class="iconoir-mail"></i> {{ $t('rowApprovals.sendEmail') }}
          </a>
        </div>

        <div class="row-approvals__actions">
          <a
            v-if="approval.status !== 'revoked'"
            class="row-approvals__action"
            :class="{
              'row-approvals__action--accent':
                approval.status === 'changes_requested',
            }"
            @click="roundFor = roundFor === approval.id ? null : approval.id"
          >
            <i class="iconoir-refresh-double"></i>
            {{ $t('rowApprovals.newRound') }}
          </a>
          <a
            v-if="approval.status === 'pending'"
            class="row-approvals__action"
            @click="manualFor = manualFor === approval.id ? null : approval.id"
          >
            <i class="iconoir-attachment"></i>
            {{ $t('rowApprovals.manual') }}
          </a>
          <a
            class="row-approvals__action"
            :class="{
              'row-approvals__action--danger': approval.status !== 'revoked',
            }"
            @click="toggleRevoked(approval)"
          >
            <i class="iconoir-lock"></i>
            {{
              approval.status === 'revoked'
                ? $t('rowApprovals.reactivate')
                : $t('rowApprovals.revoke')
            }}
          </a>
        </div>

        <!-- Nova rodada: nota do que mudou + arquivos opcionais -->
        <div v-if="roundFor === approval.id" class="row-approvals__manual">
          <div class="row-approvals__manual-title">
            {{ $t('rowApprovals.roundTitle') }}
          </div>
          <textarea
            v-model="roundNote"
            class="input row-approvals__textarea"
            rows="3"
            :placeholder="$t('rowApprovals.roundNote')"
          ></textarea>
          <div class="row-approvals__files">
            <span
              v-for="file in roundFiles"
              :key="file.name"
              class="row-approvals__file-chip"
            >
              <i class="iconoir-attachment"></i> {{ file.visibleName }}
              <a
                class="row-approvals__file-remove"
                @click="removeRoundFile(file.name)"
                ><i class="iconoir-cancel"></i
              ></a>
            </span>
            <a class="row-approvals__action" @click="pickRoundFile()">
              <i class="iconoir-plus"></i>
              {{ $t('rowApprovals.roundAddFile') }}
            </a>
          </div>
          <div class="row-approvals__manual-actions">
            <Button type="secondary" size="small" @click="roundFor = null">
              {{ $t('rowApprovals.cancel') }}
            </Button>
            <Button
              type="primary"
              size="small"
              :loading="savingRound"
              :disabled="!roundNote.trim() && roundFiles.length === 0"
              @click="submitRound(approval)"
            >
              {{ $t('rowApprovals.roundSend') }}
            </Button>
          </div>
        </div>

        <!-- Registro manual com evidência -->
        <div v-if="manualFor === approval.id" class="row-approvals__manual">
          <div class="row-approvals__manual-title">
            {{ $t('rowApprovals.manualTitle') }}
          </div>
          <div class="row-approvals__manual-row">
            <RadioGroup
              v-model="manualDecision"
              :options="[
                {
                  label: $t('rowApprovals.manualApproved'),
                  value: 'approved',
                },
                {
                  label: $t('rowApprovals.manualChanges'),
                  value: 'changes_requested',
                },
              ]"
            ></RadioGroup>
          </div>
          <input
            v-model="manualName"
            class="input"
            :placeholder="$t('rowApprovals.manualName')"
          />
          <input
            v-model="manualContact"
            class="input"
            :placeholder="$t('rowApprovals.manualContact')"
          />
          <input
            v-model="manualComment"
            class="input"
            :placeholder="$t('rowApprovals.manualComment')"
          />
          <div class="row-approvals__manual-evidence">
            <a class="row-approvals__action" @click="pickEvidence()">
              <i class="iconoir-attachment"></i>
              {{
                manualEvidence
                  ? manualEvidence.visibleName
                  : $t('rowApprovals.manualEvidence')
              }}
            </a>
          </div>
          <div class="row-approvals__manual-actions">
            <Button type="secondary" size="small" @click="manualFor = null">
              {{ $t('rowApprovals.cancel') }}
            </Button>
            <Button
              type="primary"
              size="small"
              :loading="savingManual"
              :disabled="!manualName.trim()"
              @click="submitManual(approval)"
            >
              {{ $t('rowApprovals.manualSave') }}
            </Button>
          </div>
        </div>

        <!-- Assinaturas -->
        <ul
          v-if="approval.decisions.length > 0"
          class="row-approvals__decisions"
        >
          <li v-for="decision in approval.decisions" :key="decision.id">
            <span
              class="row-approvals__decision-dot"
              :class="
                decision.decision === 'approved'
                  ? 'row-approvals__decision-dot--approved'
                  : 'row-approvals__decision-dot--changes'
              "
            ></span>
            <span class="row-approvals__decision-body">
              <strong>{{ decision.approver_name || '?' }}</strong>
              {{
                decision.decision === 'approved'
                  ? $t('rowApprovals.decisionApproved')
                  : $t('rowApprovals.decisionChanges')
              }}
              <template v-if="decision.round">
                · {{ $t('rowApprovals.round', { number: decision.round }) }}
              </template>
              · {{ formatDate(decision.created_on) }}
              <span v-if="decision.manual" class="row-approvals__manual-flag">
                {{ $t('rowApprovals.manualFlag') }}
              </span>
              <em v-if="decision.comment">“{{ decision.comment }}”</em>
              <span class="row-approvals__decision-meta">
                <code>{{ decision.verification_code }}</code>
                <a
                  v-if="decision.receipt_url"
                  :href="decision.receipt_url"
                  target="_blank"
                  rel="noopener"
                  ><i class="iconoir-download"></i>
                  {{ $t('rowApprovals.receipt') }}</a
                >
                <a
                  v-if="decision.evidence_url"
                  :href="decision.evidence_url"
                  target="_blank"
                  rel="noopener"
                  >{{ $t('rowApprovals.evidence') }}</a
                >
              </span>
            </span>
          </li>
        </ul>
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      :accept="filePickTarget === 'evidence' ? 'image/*' : undefined"
      :multiple="filePickTarget === 'round'"
      class="row-approvals__file-input"
      @change="onFilePicked"
    />

    <RequestRatingModal
      ref="ratingModal"
      :row="row"
      :table="table"
      :fields="fields"
      @created="onRatingCreated"
    ></RequestRatingModal>

    <CreateChecklistModal
      ref="checklistModal"
      :row="row"
      :table="table"
      :fields="fields"
      @created="onChecklistCreated"
    ></CreateChecklistModal>

    <SendApprovalModal
      ref="sendModal"
      :row="row"
      :table="table"
      :fields="fields"
      @created="onCreated"
    ></SendApprovalModal>
  </div>
</template>

<script>
import ApprovalsService from '@baserow/modules/core/services/approvals'
import UserFileService from '@baserow/modules/core/services/userFile'
import { copyToClipboard } from '@baserow/modules/database/utils/clipboard'
import { notifyIf } from '@baserow/modules/core/utils/error'
import SendApprovalModal from '@baserow/modules/database/components/row/SendApprovalModal'
import CreateChecklistModal from '@baserow/modules/database/components/row/CreateChecklistModal'
import RequestRatingModal from '@baserow/modules/database/components/row/RequestRatingModal'
import {
  buildApprovalShareMessage,
  buildChecklistShareMessage,
  buildRatingShareMessage,
} from '@baserow/modules/database/utils/approvalShare'

export default {
  name: 'RowApprovalsSidebar',
  components: { SendApprovalModal, CreateChecklistModal, RequestRatingModal },
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    row: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      approvals: [],
      checklists: [],
      ratings: [],
      requestFormUrl: null,
      sendingEmailId: null,
      roundFor: null,
      roundNote: '',
      roundFiles: [],
      savingRound: false,
      manualFor: null,
      manualDecision: 'approved',
      manualName: '',
      manualContact: '',
      manualComment: '',
      manualEvidence: null,
      savingManual: false,
      // 'round' ou 'evidence' — para onde vai o próximo arquivo escolhido.
      filePickTarget: null,
    }
  },
  computed: {
    service() {
      return ApprovalsService(this.$client)
    },
  },
  watch: {
    'row.id'() {
      this.fetch()
    },
  },
  mounted() {
    this.fetch()
  },
  methods: {
    async fetch() {
      if (!this.row.id || this.row.id < 0) {
        this.loading = false
        return
      }
      this.loading = true
      try {
        const [approvals, checklists, ratings] = await Promise.all([
          this.service.fetchAll(this.table.id, this.row.id),
          this.service.fetchChecklists(this.table.id, this.row.id),
          this.service.fetchRatings(this.table.id, this.row.id),
        ])
        this.approvals = approvals.data.approvals
        this.checklists = checklists.data.checklists
        this.ratings = ratings.data.ratings
      } catch (error) {
        notifyIf(error, 'rowApprovals')
      }
      this.loading = false
    },
    onRatingCreated(rating) {
      this.ratings = [rating, ...this.ratings]
    },
    scoreKind(score) {
      if (score >= 9) return 'high'
      if (score >= 7) return 'mid'
      return 'low'
    },
    openRatingWhatsApp(rating) {
      const message = buildRatingShareMessage(rating)
      const digits = (rating.contact_phone || '').replace(/\D/g, '')
      const url = digits
        ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`
      window.open(url, '_blank', 'noopener')
    },
    async toggleRatingRevoked(rating) {
      try {
        const { data } = await this.service.updateRating(rating.id, {
          revoked: rating.status !== 'revoked',
        })
        const index = this.ratings.findIndex((r) => r.id === rating.id)
        if (index !== -1) this.ratings.splice(index, 1, data.rating)
      } catch (error) {
        notifyIf(error, 'rowRatings')
      }
    },
    onChecklistCreated(checklist) {
      this.checklists = [checklist, ...this.checklists]
    },
    checklistChip(checklist) {
      if (checklist.status === 'pending' && checklist.expired) return 'expired'
      return checklist.status
    },
    copyText(text) {
      copyToClipboard(text)
      this.$store.dispatch('toast/info', {
        title: this.$t('rowApprovals.copiedTitle'),
        message: this.$t('rowApprovals.copied'),
      })
    },
    openChecklistWhatsApp(checklist) {
      const message = buildChecklistShareMessage(checklist)
      const digits = (checklist.contact_phone || '').replace(/\D/g, '')
      const url = digits
        ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`
      window.open(url, '_blank', 'noopener')
    },
    async toggleItem(checklist, item) {
      try {
        const { data } = await this.service.resolveChecklistItem(
          checklist.id,
          item.id,
          { done: !item.done }
        )
        const index = this.checklists.findIndex((c) => c.id === checklist.id)
        if (index !== -1) this.checklists.splice(index, 1, data.checklist)
      } catch (error) {
        notifyIf(error, 'rowApprovals')
      }
    },
    async toggleChecklistRevoked(checklist) {
      try {
        const { data } = await this.service.updateChecklist(checklist.id, {
          revoked: checklist.status !== 'revoked',
        })
        const index = this.checklists.findIndex((c) => c.id === checklist.id)
        if (index !== -1) this.checklists.splice(index, 1, data.checklist)
      } catch (error) {
        notifyIf(error, 'rowApprovals')
      }
    },
    async openRequestForm() {
      try {
        const { data } = await this.service.ensureRequestForm(this.table.id)
        this.requestFormUrl = data.url
        this.$store.dispatch('toast/info', {
          title: this.$t('rowChecklists.requestFormTitle'),
          message: this.$t('rowChecklists.requestFormCreated'),
        })
      } catch (error) {
        notifyIf(error, 'rowApprovals')
      }
    },
    onCreated(approval) {
      this.approvals = [approval, ...this.approvals]
    },
    chipKind(approval) {
      if (approval.status === 'pending' && approval.expired) {
        return 'expired'
      }
      return approval.status
    },
    chipLabel(approval) {
      return this.$t(`rowApprovals.status.${this.chipKind(approval)}`)
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (e) {
        return iso
      }
    },
    copyLink(approval) {
      copyToClipboard(approval.public_url)
      this.$store.dispatch('toast/info', {
        title: this.$t('rowApprovals.copiedTitle'),
        message: this.$t('rowApprovals.copied'),
      })
    },
    openWhatsApp(approval) {
      const message = buildApprovalShareMessage(approval)
      const digits = (approval.contact_phone || '').replace(/\D/g, '')
      const url = digits
        ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`
      window.open(url, '_blank', 'noopener')
    },
    async sendEmail(approval) {
      if (!approval.contact_email || this.sendingEmailId === approval.id) {
        return
      }
      this.sendingEmailId = approval.id
      try {
        const { data } = await this.service.sendEmail(
          approval.id,
          approval.contact_email
        )
        this.$store.dispatch('toast/info', {
          title: this.$t('rowApprovals.emailSentTitle'),
          message:
            data.sent_via === 'gmail'
              ? this.$t('rowApprovals.emailSentGmail')
              : this.$t('rowApprovals.emailSentSmtp'),
        })
      } catch (error) {
        notifyIf(error, 'rowApprovals')
      }
      this.sendingEmailId = null
    },
    pickRoundFile() {
      this.filePickTarget = 'round'
      this.$refs.fileInput.click()
    },
    pickEvidence() {
      this.filePickTarget = 'evidence'
      this.$refs.fileInput.click()
    },
    removeRoundFile(name) {
      this.roundFiles = this.roundFiles.filter((f) => f.name !== name)
    },
    async onFilePicked(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      for (const file of files) {
        try {
          const { data: userFile } = await UserFileService(
            this.$client
          ).uploadFile(file)
          const entry = { name: userFile.name, visibleName: file.name }
          if (this.filePickTarget === 'evidence') {
            this.manualEvidence = entry
          } else {
            this.roundFiles.push(entry)
          }
        } catch (error) {
          notifyIf(error, 'rowApprovals')
        }
      }
    },
    async submitRound(approval) {
      this.savingRound = true
      try {
        const { data } = await this.service.addRound(approval.id, {
          note: this.roundNote,
          files: this.roundFiles.map((f) => ({
            name: f.name,
            visible_name: f.visibleName,
          })),
        })
        this.replaceApproval(data.approval)
        this.roundFor = null
        this.roundNote = ''
        this.roundFiles = []
        this.$store.dispatch('toast/info', {
          title: this.$t('rowApprovals.roundSentTitle'),
          message: this.$t('rowApprovals.roundSent'),
        })
      } catch (error) {
        notifyIf(error, 'rowApprovals')
      }
      this.savingRound = false
    },
    async toggleRevoked(approval) {
      try {
        const { data } = await this.service.update(approval.id, {
          revoked: approval.status !== 'revoked',
        })
        this.replaceApproval(data.approval)
      } catch (error) {
        notifyIf(error, 'rowApprovals')
      }
    },
    async submitManual(approval) {
      this.savingManual = true
      try {
        const { data } = await this.service.manualDecision(approval.id, {
          decision: this.manualDecision,
          approver_name: this.manualName,
          signer_contact: this.manualContact,
          comment: this.manualComment,
          evidence_file_name: this.manualEvidence
            ? this.manualEvidence.name
            : null,
        })
        this.replaceApproval(data.approval)
        this.manualFor = null
        this.manualName = ''
        this.manualContact = ''
        this.manualComment = ''
        this.manualEvidence = null
      } catch (error) {
        notifyIf(error, 'rowApprovals')
      }
      this.savingManual = false
    },
    replaceApproval(updated) {
      const index = this.approvals.findIndex((a) => a.id === updated.id)
      if (index !== -1) {
        this.approvals.splice(index, 1, updated)
      }
    },
  },
}
</script>
